import React from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  useTheme,
  Alert,
  Collapse,
  CssBaseline,
  Link,
  Grid
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTriangleExclamation,
  faAngleDown,
  faAngleUp,
  faRefresh
} from "@fortawesome/sharp-solid-svg-icons";
import { getEnvironmentVariable } from "../../global/environmentVariables";

export default function ErrorFallback({ resetError, error }) {
  const theme = useTheme();
  const [showDetails, setShowDetails] = React.useState(false);

  const getUserFriendlyMessage = (error) => {
    if (!error)
      return "We're sorry, but something unexpected happened. Pleasee try refreshing the page.";

    const errorString = String(error);

    if (
      errorString.includes("Network Error") ||
      errorString.includes("Failed to fetch")
    ) {
      return "Oops! We're having trouble connecting you to our services. Please check your internet connection and give it another try.";
    }

    if (errorString.includes("404") || errorString.includes("Not Found")) {
      return "Hmm, we can't seem to find what you're looking for. It might have moved or doesn't exist anymore.";
    }

    if (
      errorString.includes("ChunkLoadError") ||
      errorString.includes("Loading chunk")
    ) {
      return "Sorry, we couldn't load some important files. A quick page refresh should fix this right up!";
    }

    return "We're sorry, but something unexpected happened. Please try refreshing the page";
  };

  const userMessage = getUserFriendlyMessage(error);
  const technicalDetails = error ? String(error) : "Unknown error";

  const SYSTEM_IMAGES_CLOUDFRONT = getEnvironmentVariable(
    "SYSTEM_IMAGES_CLOUDFRONT"
  );
  const HAS_LARGE_BANNER_LOGO =
    getEnvironmentVariable("HAS_LARGE_BANNER_LOGO") === "true";

  return (
    <Box>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "background.default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
          px: 2
        }}
      >
        <Container maxWidth="lg">
          <Grid container justifyContent="center">
            <Grid item xs={12} sm={10} md={8} lg={6}>
              <Paper
                elevation={2}
                sx={{
                  padding: { xs: 3, sm: 4, md: 5 },
                  textAlign: "center",
                  borderRadius: theme.shape.borderRadius || 2,
                  bgcolor: "background.secondary",
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: theme.shadows[2]
                }}
              >
                <Box sx={{ mb: { xs: 3, sm: 4 } }}>
                  <Link href="/" underline="none">
                    <img
                      src={`${SYSTEM_IMAGES_CLOUDFRONT}/images/BannerLogo${
                        theme.palette.mode === "dark" ? "Dark" : ""
                      }${HAS_LARGE_BANNER_LOGO ? "Large" : ""}.png`}
                      alt="logo"
                      style={{
                        maxHeight: "40px",
                        opacity: 0.8
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </Link>
                </Box>
                <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    style={{
                      fontSize: "4rem",
                      color: theme.palette.error?.main || "#d32f2f",
                      opacity: 0.85
                    }}
                  />
                </Box>

                <Typography
                  variant="h3"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                    letterSpacing: "-0.06px",
                    color: "text.primary",
                    mb: 2
                  }}
                >
                  Oops! Something went wrong
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    mb: 4,
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                    lineHeight: 1.6,
                    maxWidth: "400px",
                    mx: "auto"
                  }}
                >
                  {userMessage}
                </Typography>

                <Box
                  sx={{
                    mb: 4,
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    justifyContent: "center"
                  }}
                >
                  <Button
                    variant="body1"
                    color="primary"
                    size="large"
                    onClick={() => window.location.reload()}
                    startIcon={<FontAwesomeIcon icon={faRefresh} />}
                    sx={{
                      textTransform: "none",
                      fontSize: "16px",
                      py: 1.5,
                      px: 3,
                      borderRadius: theme.shape.borderRadius || 2,
                      minWidth: 160
                    }}
                  >
                    Refresh Page
                  </Button>

                  <Button
                    variant="text"
                    color="primary"
                    size="large"
                    onClick={() => window.history.back()}
                    sx={{
                      textTransform: "none",
                      fontSize: "16px",
                      py: 1.5,
                      px: 3,
                      borderRadius: theme.shape.borderRadius || 2,
                      minWidth: 100
                    }}
                  >
                    Go Back
                  </Button>
                </Box>

                <Box
                  sx={{
                    mt: 4,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    pt: 3
                  }}
                >
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowDetails(!showDetails)}
                    endIcon={
                      <FontAwesomeIcon
                        icon={showDetails ? faAngleUp : faAngleDown}
                        style={{ fontSize: "12px" }}
                      />
                    }
                    sx={{
                      color: "text.secondary",
                      textTransform: "none",
                      fontSize: "14px",
                      "&:hover": {
                        color: "text.primary"
                      }
                    }}
                  >
                    {showDetails
                      ? "Hide technical details"
                      : "Show technical details"}
                  </Button>

                  <Collapse in={showDetails}>
                    <Alert
                      severity="error"
                      variant="outlined"
                      sx={{
                        mt: 2,
                        textAlign: "left",
                        backgroundColor: "background.primary",
                        borderRadius: theme.shape.borderRadius || 1
                      }}
                    >
                      <Typography
                        variant="caption"
                        component="div"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "11px",
                          wordBreak: "break-word",
                          whiteSpace: "pre-wrap",
                          color: "text.primary",
                          lineHeight: 1.4
                        }}
                      >
                        {technicalDetails}
                      </Typography>
                    </Alert>
                  </Collapse>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    mt: 4,
                    fontSize: "14px",
                    lineHeight: 1.5
                  }}
                >
                  Need help? Our support team is here to assist you, or you can
                  try refreshing your browser.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}
